import React, { useRef, useEffect } from 'react';
import { Star, Clock, Bookmark, Check } from 'lucide-react';

const CollectionMenu = ({ isOpen, onClose, onSelect, position, activeCollection }) => {
  const menuRef = useRef(null);

  // Close when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const options = [
    {
      id: 'favourite',
      icon: Star,
      label: 'Favourite',
      color: 'text-yellow-400',
      description: 'Add to favourites'
    },
    {
      id: 'read-later',
      icon: Clock,
      label: 'Read Later',
      color: 'text-blue-400',
      description: 'Save for later'
    },
    {
      id: 'save',
      icon: Bookmark,
      label: 'Save',
      color: 'text-purple-400',
      description: 'Add to saved'
    }
  ];

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-40" onClick={onClose} />
      
      {/* Arc Menu - Left Side */}
      <div
        ref={menuRef}
        className="fixed z-50 animate-scale-in"
        style={{
          top: `${position.top}px`,
          left: `${position.left}px`,
          transform: 'translateY(-50%)',
        }}
      >
        <div className="relative">
          {/* Arc Container */}
          <div className="relative flex items-center justify-center">
            {options.map((option, index) => {
              const Icon = option.icon;
              const isActive = activeCollection === option.id;
              
              // Calculate positions for arc formation on LEFT side (120° to 240° range)
              const angle = (index * 60 + 120) * (Math.PI / 180);
              const radius = 90;
              const x = Math.cos(angle) * radius;
              const y = Math.sin(angle) * radius;
              
              return (
                <button
                  key={option.id}
                  onClick={() => {
                    onSelect(option.id);
                    onClose();
                  }}
                  className="absolute flex flex-col items-center transition-all duration-300 group"
                  style={{
                    transform: `translate(${x}px, ${y}px)`,
                    animationDelay: `${index * 50}ms`,
                  }}
                >
                  {/* Icon with black background and white border */}
                  <div className={`w-12 h-12 rounded-full bg-black border-2 flex items-center justify-center transition-all duration-300 group-hover:scale-110 group-hover:rotate-12 shadow-lg relative ${
                    isActive ? 'border-white ring-2 ring-offset-2 ring-white' : 'border-white'
                  }`}>
                    <Icon className="w-6 h-6 text-white" strokeWidth={2} />
                    {isActive && (
                      <div className="absolute -top-1 -right-1 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center border-2 border-black">
                        <Check className="w-3 h-3 text-white" strokeWidth={3} />
                      </div>
                    )}
                  </div>
                  {/* Label */}
                  <div className={`mt-1 text-center backdrop-blur-sm rounded-lg px-2 py-1 border ${
                    isActive 
                      ? 'bg-white/90 border-white/40' 
                      : 'bg-black/80 border-white/20'
                  }`}>
                    <p className={`text-xs font-semibold whitespace-nowrap ${
                      isActive ? 'text-gray-900' : 'text-white'
                    }`}>
                      {option.label}
                      {isActive && ' ✓'}
                    </p>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
};

// Add animation styles
const styles = document.createElement('style');
styles.innerHTML = `
  @keyframes scale-in {
    from {
      opacity: 0;
      transform: translateY(-50%) scale(0.8);
    }
    to {
      opacity: 1;
      transform: translateY(-50%) scale(1);
    }
  }
  
  .animate-scale-in {
    animation: scale-in 0.2s cubic-bezier(0.16, 1, 0.3, 1);
  }
`;

if (!document.getElementById('collection-menu-styles')) {
  styles.id = 'collection-menu-styles';
  document.head.appendChild(styles);
}

export default CollectionMenu;